import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Loading from "../Loading";
import { getSizes } from "../utils";

const ITEMS_PER_ROW = 3;
const SECTION_TITLE = "Gallery";

const GalleryContainer = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(1, 1fr);
  @media only screen and (min-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
  gap: 1rem;
  > * {
    justify-self: center;
    width: auto;
    max-height: 200px;
    object-fit: cover;
  }
`;

const LazyImage = styled.img`
  &.img-loading {
    visibility: hidden;
    position: "absolute";
    cursor: wait;
  }
  cursor: pointer;
`;

const SeeMoreButton = ({ totalPhotos, totalVisiblePhotos, onClick }) => {
  return (
    <div className="mt-4 text-center w-100">
      <button
        className="btn btn-link"
        title={`See all ${totalPhotos} photos`}
        onClick={onClick}
      >
        See more {totalPhotos - totalVisiblePhotos} photos
      </button>
    </div>
  );
};

const SeeLessButton = ({ onClick }) => {
  return (
    <div className="mt-4 text-center w-100">
      <button
        className="btn btn-link"
        title="See less photos"
        onClick={onClick}
      >
        See less photos
      </button>
    </div>
  );
};

const LazyLoadingImage = ({ source, onClick }) => {
  const [loading, setLoading] = useState(true);
  const [inView, setInView] = useState(false);
  const placeholderRef = useRef();
  const [photo, setPhoto] = useState(source);

  useEffect(() => {
    const observer = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setPhoto(source);
          setInView(true);
          obs.disconnect();
        }
      }
    }, {});
    observer.observe(placeholderRef.current);
    return () => {
      observer.disconnect();
    };
  }, [source]);

  return (
    <>
      {inView ? (
        <LazyImage
          key={source}
          src={photo}
          alt=""
          onLoadStart={() => setLoading(true)}
          onLoad={() => setLoading(false)}
          loading="lazy"
          className={loading ? "img-loading" : ""}
          onClick={onClick}
        />
      ) : (
        <Loading ref={placeholderRef} />
      )}
      {loading && inView && <Loading />}
    </>
  );
};

const ListingDetailsGallery = ({ listing, onClick }) => {
  const { isMobile } = getSizes();
  const itemsPerRow = isMobile ? 1 : ITEMS_PER_ROW;
  const photos = listing.ListingPhotosMidsize ?? [];
  const [visiblePhotos, setVisiblePhotos] = useState(
    photos.filter((_, index) => index < itemsPerRow)
  );
  const hasMore =
    photos.length > visiblePhotos.length && photos.length > itemsPerRow;
  const hasLess =
    photos.length === visiblePhotos.length && photos.length > itemsPerRow;

  const seeAllPhotos = () => setVisiblePhotos(photos);
  const seeInitialPhotos = () =>
    setVisiblePhotos(photos.filter((_, index) => index < itemsPerRow));
  return (
    <div id="Gallery" className="row mg-topbtm-40 txt-algn-lft">
      <div className="col-12 mg-topbtm-20">
        <h3>{SECTION_TITLE}</h3>
      </div>
      <GalleryContainer className="col-12">
        {visiblePhotos.map((photo, index) => (
          <LazyLoadingImage
            key={photo}
            source={photo}
            onClick={() => onClick(index)}
          />
        ))}
      </GalleryContainer>
      {hasMore && (
        <SeeMoreButton
          totalPhotos={photos.length}
          totalVisiblePhotos={visiblePhotos.length}
          onClick={seeAllPhotos}
        />
      )}
      {hasLess && <SeeLessButton onClick={seeInitialPhotos} />}
    </div>
  );
};

export default ListingDetailsGallery;
